/**
 * Painel de Análise de Padrões de Fala
 * Identifica hesitações, interrupções, repetições e outros padrões
 */

import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@mui/material'
import GraphicEqIcon from '@mui/icons-material/GraphicEq'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import BlockIcon from '@mui/icons-material/Block'
import ReplayIcon from '@mui/icons-material/Replay'
import CancelIcon from '@mui/icons-material/Cancel'
import CampaignIcon from '@mui/icons-material/Campaign'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import RefreshIcon from '@mui/icons-material/Refresh'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import PersonIcon from '@mui/icons-material/Person'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { SpeechPatternAnalysis, SpeechPattern, SpeechPatternType } from '../../types/transcription'
import { getSpeechPatternAnalysis } from '../../services/api'
import { contextualErrors, FriendlyError } from '../../services/errorHandler'
import AnalysisNotAvailableCard from './AnalysisNotAvailableCard'

type Props = {
  fileId: string
  onNavigateToSegment?: (segmentIndex: number) => void
}

const PATTERN_CONFIG: Record<SpeechPatternType, {
  label: string
  icon: React.ElementType
  color: string
  bgColor: string
  description: string
}> = {
  hesitation: {
    label: 'Hesitação',
    icon: PauseCircleOutlineIcon,
    color: '#9c27b0',
    bgColor: '#f3e5f5',
    description: 'Pausas e marcadores de incerteza (éé, hmm, bem...)'
  },
  interruption: {
    label: 'Interrupção',
    icon: BlockIcon,
    color: '#f44336',
    bgColor: '#ffebee',
    description: 'Falante cortado ou sobreposto por outro'
  },
  repetition: {
    label: 'Repetição',
    icon: ReplayIcon,
    color: '#ff9800',
    bgColor: '#fff3e0',
    description: 'Palavras ou frases repetidas'
  },
  negation: {
    label: 'Negação',
    icon: CancelIcon,
    color: '#795548',
    bgColor: '#efebe9',
    description: 'Negativas enfáticas ou persistentes'
  },
  emphasis: {
    label: 'Ênfase',
    icon: CampaignIcon,
    color: '#2196f3',
    bgColor: '#e3f2fd',
    description: 'Tom elevado ou palavras enfatizadas'
  },
  uncertainty: {
    label: 'Incerteza',
    icon: HelpOutlineIcon,
    color: '#607d8b',
    bgColor: '#eceff1',
    description: 'Expressões de dúvida (acho que, talvez, não sei...)'
  },
  aggression: {
    label: 'Agressividade',
    icon: WarningAmberIcon,
    color: '#d32f2f',
    bgColor: '#ffcdd2',
    description: 'Tom agressivo ou linguagem hostil'
  }
}

const SIGNIFICANCE_CONFIG: Record<string, { label: string; color: string; weight: number }> = {
  high: { label: 'Alta', color: '#d32f2f', weight: 3 },
  medium: { label: 'Média', color: '#ff9800', weight: 2 },
  low: { label: 'Baixa', color: '#4caf50', weight: 1 }
}

/**
 * Formata o tempo para exibição.
 * Aceita tanto segundos (número ou string numérica) quanto formato HH:MM:SS.
 */
function formatTime(time: string): string {
  if (!time) return '0:00'
  
  // Se já está no formato HH:MM:SS ou MM:SS, retorna diretamente (ou simplificado)
  if (time.includes(':')) {
    const parts = time.split(':')
    if (parts.length === 3) {
      // HH:MM:SS - remover horas se for 00
      const hours = parseInt(parts[0], 10)
      const mins = parseInt(parts[1], 10)
      const secs = parseInt(parts[2], 10)
      if (hours > 0) {
        return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      }
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }
    // MM:SS - retorna como está
    return time
  }
  
  // Se for número de segundos
  const seconds = parseFloat(time)
  if (isNaN(seconds)) return '0:00'
  
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function PatternCard({ pattern, expanded, onToggle, onNavigateToSegment }: {
  pattern: SpeechPattern
  expanded: boolean
  onToggle: () => void
  onNavigateToSegment?: (segmentIndex: number) => void
}) {
  const config = PATTERN_CONFIG[pattern.type] || PATTERN_CONFIG.hesitation
  const Icon = config.icon
  const sigConfig = SIGNIFICANCE_CONFIG[pattern.significance] || SIGNIFICANCE_CONFIG.medium

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1.5,
        border: '1px solid #e0e0e0',
        borderLeft: `4px solid ${config.color}`,
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 1
        }
      }}
    >
      <Box
        sx={{
          p: 2,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2
        }}
        onClick={onToggle}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: config.bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <Icon sx={{ color: config.color, fontSize: 20 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="caption" sx={{
              bgcolor: '#f5f5f5',
              px: 1,
              py: 0.25,
              borderRadius: 0.5,
              fontFamily: 'monospace'
            }}>
              {formatTime(pattern.startTime)}
            </Typography>
            <Chip
              label={config.label}
              size="small"
              sx={{
                bgcolor: config.bgColor,
                color: config.color,
                fontWeight: 600,
                fontSize: '0.65rem',
                height: 20
              }}
            />
            <Chip
              label={sigConfig.label}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.6rem',
                height: 18,
                color: sigConfig.color,
                borderColor: sigConfig.color
              }}
            />
          </Box>

          <Typography
            variant="body2"
            sx={{
              bgcolor: '#fafafa',
              p: 1,
              borderRadius: 1,
              fontStyle: 'italic',
              fontSize: '0.85rem',
              whiteSpace: 'pre-wrap'
            }}
          >
            "{pattern.text}"
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <PersonIcon sx={{ fontSize: 14, color: '#666' }} />
            <Typography variant="caption" sx={{ color: '#666' }}>
              {pattern.speaker}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {onNavigateToSegment && (
            <Tooltip title="Ir para o segmento na transcrição">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  onNavigateToSegment(pattern.segmentIndex)
                }}
                sx={{ 
                  color: config.color,
                  '&:hover': { bgcolor: config.bgColor }
                }}
              >
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2, pt: 0, ml: 7 }}>
          <Typography variant="body2" sx={{ color: '#666' }}>
            <strong>Contexto:</strong> {pattern.context}
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  )
}

export default function SpeechPatternPanel({ fileId, onNavigateToSegment }: Props) {
  const [analysis, setAnalysis] = useState<SpeechPatternAnalysis | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [filter, setFilter] = useState<SpeechPatternType | 'all'>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [viewTab, setViewTab] = useState(0)

  const loadAnalysis = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getSpeechPatternAnalysis(fileId)
      setAnalysis(data)
    } catch (e: any) {
      const friendlyError = contextualErrors.analysis(e)
      setError(friendlyError)
      console.error('Erro ao carregar análise:', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAnalysis()
  }, [fileId])

  if (loading) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={48} />
          <Typography sx={{ mt: 2, color: '#666' }}>
            Analisando padrões de fala...
          </Typography>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Box sx={{ fontSize: 48, mb: 2 }}>⚠️</Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {error.title}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            {error.message}
          </Typography>
          {error.canRetry && (
            <Button variant="outlined" onClick={loadAnalysis} startIcon={<RefreshIcon />}>
              Tentar novamente
            </Button>
          )}
        </CardContent>
      </Card>
    )
  }

  if (!analysis) {
    return (
      <AnalysisNotAvailableCard 
        title="Análise de Padrões de Fala" 
        onRetry={loadAnalysis} 
      />
    )
  }

  const filteredPatterns = filter === 'all'
    ? analysis.patterns
    : analysis.byType[filter] || []

  const patternTypes = Object.keys(PATTERN_CONFIG) as SpeechPatternType[]
  const speakers = Object.keys(analysis.bySpeaker)

  return (
    <Box>
      {/* Header */}
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GraphicEqIcon sx={{ color: '#9c27b0' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Análise de Padrões de Fala
              </Typography>
            </Box>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadAnalysis}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Atualizar
            </Button>
          </Box>

          {/* Estatísticas gerais */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#9c27b0' }}>
                  {analysis.statistics.totalPatterns}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Padrões Detectados
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                  {analysis.patterns.filter(p => p.significance === 'high').length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Alta Significância
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#f3e5f5' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#7b1fa2' }}>
                  {analysis.statistics.mostCommonType ? PATTERN_CONFIG[analysis.statistics.mostCommonType]?.label || '-' : '-'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Padrão Mais Comum
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
                  {analysis.statistics.speakerWithMostPatterns || '-'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Mais Padrões
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Tabs */}
          <Tabs 
            value={viewTab} 
            onChange={(_, v) => setViewTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Por Tipo" sx={{ textTransform: 'none' }} />
            <Tab label="Por Falante" sx={{ textTransform: 'none' }} />
            <Tab label="Insights" sx={{ textTransform: 'none' }} />
          </Tabs>
        </CardContent>
      </Card>

      {/* Tab: Por Tipo */}
      {viewTab === 0 && (
        <>
          {/* Filtros por tipo */}
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0', mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Filtrar por Tipo
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                <Chip
                  label={`Todos (${analysis.patterns.length})`}
                  onClick={() => setFilter('all')}
                  color={filter === 'all' ? 'primary' : 'default'}
                  variant={filter === 'all' ? 'filled' : 'outlined'}
                />
                {patternTypes.map((type) => {
                  const config = PATTERN_CONFIG[type]
                  const Icon = config.icon
                  const count = analysis.byType[type]?.length || 0
                  if (count === 0) return null
                  
                  return (
                    <Chip
                      key={type}
                      icon={<Icon />}
                      label={`${config.label} (${count})`}
                      onClick={() => setFilter(type)}
                      sx={{
                        bgcolor: filter === type ? config.bgColor : undefined,
                        color: filter === type ? config.color : undefined,
                        borderColor: filter === type ? config.color : undefined,
                        '& .MuiChip-icon': { color: config.color }
                      }}
                      variant={filter === type ? 'filled' : 'outlined'}
                    />
                  )
                })}
              </Stack>
            </CardContent>
          </Card>

          {/* Lista de padrões */}
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                {filter === 'all' 
                  ? `Todos os Padrões (${filteredPatterns.length})`
                  : `${PATTERN_CONFIG[filter].label} (${filteredPatterns.length})`
                }
              </Typography>

              {filteredPatterns.map((pattern) => (
                <PatternCard
                  key={pattern.id}
                  pattern={pattern}
                  expanded={expandedId === pattern.id}
                  onToggle={() => setExpandedId(expandedId === pattern.id ? null : pattern.id)}
                  onNavigateToSegment={onNavigateToSegment}
                />
              ))}

              {filteredPatterns.length === 0 && (
                <Typography sx={{ textAlign: 'center', color: '#666', py: 4 }}>
                  Nenhum padrão encontrado nesta categoria
                </Typography>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Tab: Por Falante */}
      {viewTab === 1 && (
        <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 600 }}>
              Análise por Falante
            </Typography>

            {speakers.map((speaker) => {
              const speakerPatterns = analysis.bySpeaker[speaker] || []
              const total = speakerPatterns.length
              const percentage = (total / analysis.patterns.length) * 100

              // Contar por tipo
              const typeCounts: Record<string, number> = {}
              speakerPatterns.forEach(p => {
                typeCounts[p.type] = (typeCounts[p.type] || 0) + 1
              })

              return (
                <Paper
                  key={speaker}
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: '#e3f2fd',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <PersonIcon sx={{ color: '#1976d2' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {speaker}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {total} padrões ({percentage.toFixed(0)}% do total)
                      </Typography>
                    </Box>
                  </Box>

                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      mb: 2,
                      bgcolor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#9c27b0'
                      }
                    }}
                  />

                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {Object.entries(typeCounts).map(([type, count]) => {
                      const config = PATTERN_CONFIG[type as SpeechPatternType]
                      if (!config) return null
                      return (
                        <Chip
                          key={type}
                          size="small"
                          label={`${config.label}: ${count}`}
                          sx={{
                            bgcolor: config.bgColor,
                            color: config.color,
                            fontSize: '0.7rem'
                          }}
                        />
                      )
                    })}
                  </Stack>
                </Paper>
              )
            })}
          </CardContent>
        </Card>
      )}

      {/* Tab: Insights */}
      {viewTab === 2 && (
        <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <LightbulbIcon sx={{ color: '#ffc107' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Insights da Análise
              </Typography>
            </Box>

            {analysis.insights.map((insight, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: '#fffde7',
                  border: '1px solid #fff9c4',
                  borderRadius: 2
                }}
              >
                <Typography variant="body2">
                  💡 {insight}
                </Typography>
              </Paper>
            ))}

            {analysis.insights.length === 0 && (
              <Typography sx={{ textAlign: 'center', color: '#666', py: 4 }}>
                Nenhum insight disponível
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Legenda */}
      <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="caption" sx={{ color: '#666' }}>
          💡 <strong>Dica:</strong> Padrões de fala podem indicar desconforto, incerteza ou tentativas de evasão.
          Padrões de alta significância merecem atenção especial durante a análise.
          {onNavigateToSegment && ' Use o botão ➔ para ir diretamente ao segmento na aba Transcrição.'}
        </Typography>
      </Box>
    </Box>
  )
}
